import { computed, ref, watchEffect } from 'vue'
import { defineStore } from 'pinia'
import { v4 as uuidv4 } from 'uuid'

import type {
  CardAddons,
  CardTimeSlot,
} from '@/pages/booking-funnel/packageConfiguration/types/packageData'
import { getCartPrices, getPackageDetails, getPaymentUrl } from '@/services/ShopAndBook'
import { useConfigurations } from './configurations'
import { useUserStore } from './user'
import { createBooking } from '@/services/createBooking/createBooking'
import { usePackagesStore } from './packages'
import { useConfirmationStore } from './ConfirmationStore'
import { addQueryParamsToRelativeURL, calculPercentageToPay } from '@/utils/utils'
import { useStorage } from '@vueuse/core'
import type { CartSummary } from '@/components/DbReservationSummary/DbReservationSummaryTypes'
import { formatPrice } from '@/utils/currency'
import { createRequestPricingObject } from '@/services/price-booking/price-booking'
import type { APIResponsePaymentPrices } from '@delphi-direct/proxy-api-client/dist/ModelAPIProxy'

type PackageDetails = Awaited<ReturnType<typeof getPackageDetails>>['packageDetails']

interface Cart {
  date: Date
  attendees: number
  packageId: string
  functionRoomId?: string
  functionRoomSetup?: string
  addons?: CardAddons
  timeSlot?: CardTimeSlot
}

type StoredCartSummaries = Record<
  string,
  {
    timestamp: number
    cart: CartSummary
  }
>

interface BestFitModeData {
  listTimeSlot: {
    startTime: string
    endTime: string
  }[]
  priceCorrespondence: number
  listAddOnID: string[]
}

export interface BestFitModeTimeSlot {
  defaultSelected?: boolean
  startTime: string
  endTime: string
  functionRoomId: string
  originalIndex: number
  priceCorrespondence?: number
  functionRoomPrice?: number
  listAddOnID?: string[]
}

interface BestFitMode {
  listFunctionRoomID: Map<string, BestFitModeData | undefined>
  sortedTimeSlot: BestFitModeTimeSlot[]
}

export const useCartStore = defineStore('cart', () => {
  const checkoutLoading = ref(false)
  const loadingApiDetails = ref(false)
  const whiteListLocationIDMessage = ref<string | undefined>(undefined)
  const bestFitMode = ref<BestFitMode>({
    listFunctionRoomID: new Map<string, BestFitModeData>(),
    sortedTimeSlot: [],
  })

  const configStore = useConfigurations()
  const userStore = useUserStore()
  const packageStore = usePackagesStore()
  const confirmationStore = useConfirmationStore()

  /**
   * We can not get the booking information from the API
   * ("Get Booking immediately after create will always result in no revenue being returned")
   * so we store it in localStorage to display on confirmation page
   *
   * To be able to store several of them, we map them by a unique id
   */
  const storedCartSummaries = useStorage<StoredCartSummaries>(
    'db2-cart-summaries',
    {},
    localStorage,
  )

  // maximum time (milliseconds) where cart store should be available
  const cart_max_time = 24 * 60 * 60 * 1000

  const options = ref({
    needGuestrooms: false,
    guestroomsQuantity: 0,
    comments: '',
    eventName: '',
  })

  const cart = ref<Cart | null>(null)

  const packageDetails = ref<undefined | PackageDetails>(undefined)

  const isLoading = computed<boolean>(() => {
    return loadingApiDetails.value || packageStore.apiLoading
  })

  /** Sometimes only the cart will be loading but not other data */
  const isCartSummaryLoading = computed<boolean>(() => {
    return isLoading.value || apiPricing.value.isLoading
  })

  const apiPricing = ref<{
    isLoading: boolean
    data?: APIResponsePaymentPrices
  }>({
    isLoading: false,
  })

  const packageEvents = computed(() => {
    return packageDetails.value?.packageEvents
  })

  function setAddons(selectedAddons: CardAddons) {
    if (!cart.value) {
      throw new Error('Cart not initialized')
    }
    cart.value.addons = selectedAddons
  }

  const hasCart = computed<boolean>(() => {
    return cart.value ? true : false
  })

  /**
   * Aggregate data to display cart summary
   * in DbReservationSummary component
   */
  const cartSummary = computed<CartSummary | undefined>(() => {
    if (confirmationStore.cartId) {
      return getStoredCartSummary(confirmationStore.cartId)
    }

    if (!cart.value) {
      return undefined
    }
    if (!packageDetails.value) {
      return undefined
    }
    if (packageStore.apiLoading) {
      return undefined
    } // TODO loading state

    const packageInclusions = new Set<string>()
    packageDetails.value.packageEvents.forEach((event) => {
      event.items?.forEach((item) => {
        packageInclusions.add(item.name)
      })
    })

    const addons: CartSummary['addons'] = []

    if (cart.value.addons) {
      Object.keys(cart.value.addons).forEach((event) => {
        if (!cart.value?.addons) {
          return
        }
        const addonList = cart.value.addons[event]
        addonList.forEach((myAddon) => {
          // TODO need to check with SDK to have an ID for easier matching => Antoine: "easy matching" ID has been added
          // because several addons can have the same name
          const price = apiPricing.value.data?.addOns?.find((addon) => {
            return addon.addOnID === myAddon.addonId && addon.eventID === event
          })
          addons.push({
            addOnID: myAddon.addonId,
            name: myAddon.name,
            quantity: myAddon.quantity,
            totalPrice: price?.totalPrice,
            unitPrice: price?.unitPrice,
            formattedPrice: formatPrice(
              price?.totalPrice,
              myAddon.currencyCode !== ''
                ? myAddon.currencyCode
                : apiPricing.value.data?.currencyCode,
            ),
            formattedUnitPrice: formatPrice(
              price?.unitPrice,
              myAddon.currencyCode !== ''
                ? myAddon.currencyCode
                : apiPricing.value.data?.currencyCode,
            ),
          })
        })
      })
    }

    const functionRoomconfig = cart.value.functionRoomId
      ? packageStore.getDb2FunctionRoom(cart.value.packageId, cart.value.functionRoomId)
      : undefined

    const packagesStore = usePackagesStore()
    const FunctionRoomSetups = packagesStore.getDb2FunctionRoomSetups()
    const FunctionRoomSetupsLabel = FunctionRoomSetups.find(
      (item) => item.cle === cart.value?.functionRoomSetup,
    )

    const currencyCode =
      apiPricing.value.data?.currencyCode || currentPackage.value?.apiData.currencyCode
    const packagesPrice =
      apiPricing.value.data?.packagesPrice ||
      (currentPackage.value?.apiData.priceMinPerPerson
        ? currentPackage.value?.apiData.priceMinPerPerson * cart.value.attendees
        : undefined)
    const subTotal =
      apiPricing.value.data?.reservationSubTotal ||
      (currentPackage.value?.apiData.priceMinPerPerson
        ? currentPackage.value?.apiData.priceMinPerPerson * cart.value.attendees
        : undefined)
    const taxes = apiPricing.value.data?.taxes
    const gratuityFee = apiPricing.value.data?.gratuityFree
    const adminFee = apiPricing.value.data?.adminFee
    const totalReservation = apiPricing.value.data?.reservationTotal
    const [payNow, payLater] = calculPercentageToPay(
      totalReservation ?? 0,
      configStore.general.payPercentage,
    )

    return {
      packageId: currentPackage.value?.galaxyPackage?.id || '', // TODO
      db2PackageId: currentPackage.value?.apiData?.id,
      packageName: currentPackage.value?.galaxyPackage?.name || '', // TODO
      venueId: functionRoomconfig?.galaxyEventRoom?.id || '', // TODO
      venueName: functionRoomconfig?.galaxyEventRoom?.name || '', // TODO
      functionRoomSetup: FunctionRoomSetupsLabel?.name || cart.value?.functionRoomSetup,
      date: cart.value.date,
      timeSlot: cart.value.timeSlot,
      attendees: cart.value.attendees,
      currencyCode: currencyCode || '', // TODO
      inclusions: [...packageInclusions],
      addons,
      price: {
        packageBundle: packagesPrice,
        subTotal: subTotal,
        taxes: taxes,
        gratuityFee: gratuityFee,
        adminFee: adminFee,
        totalReservation: totalReservation,
      },
      formattedPricing: {
        packageBundle: formatPrice(packagesPrice, currencyCode),
        subTotal: formatPrice(subTotal, currencyCode),
        taxes: formatPrice(taxes, currencyCode),
        gratuityFee: formatPrice(gratuityFee, currencyCode),
        adminFee: formatPrice(adminFee, currencyCode),
        totalReservation: formatPrice(totalReservation, currencyCode),
        payNow: formatPrice(payNow, currencyCode),
        payLater: formatPrice(payLater, currencyCode),
      },
    }
  })

  /**
   * Start booking funnel (after package selection)
   */
  function init(date: Date, attendees: number, packageId: string) {
    cart.value = {
      date,
      attendees,
      packageId,
    }
    fetchPackageDetails()
  }

  async function fetchPackageDetails() {
    if (!cart.value) {
      throw new Error('Cart not initialized')
    }
    if (!configStore.funnel.locationId) {
      throw new Error('Missing location ID')
    }

    loadingApiDetails.value = true
    const response = await getPackageDetails(
      cart.value.packageId,
      cart.value.attendees,
      configStore.funnel.locationId,
      window.galaxyInfo?.local,
    )

    loadingApiDetails.value = false
    packageDetails.value = response.packageDetails
    /* packageDetails.value.packageEvents.forEach((event) => {
      event.addOnItems?.forEach((item) => {
        // here we can setup specific behavior for addonItems (price or description)
        // item.unitPrice = 0
      })
    }) */
  }

  /**
   * Set timeSlot in the cart
   */
  function setTimeSlot(timeSlot: CardTimeSlot) {
    if (!cart.value) {
      throw new Error('Cart not initialized')
    }
    cart.value.timeSlot = timeSlot
  }

  /**
   * Set function room in the cart
   * This will redirect the user
   */
  function setFunctionRoom(functionRoomId: string) {
    if (!cart.value) {
      throw new Error('Cart not initialized')
    }
    // Disable the "defaultSelected" TimeSlot when a new venue is selected in BestFitMode
    if (!configStore.funnel.showVenueSelection && bestFitMode.value.sortedTimeSlot !== undefined) {
      bestFitMode.value.sortedTimeSlot.forEach((e) => {
        e.defaultSelected = undefined
      })
    }
    cart.value.functionRoomId = functionRoomId
  }

  /**
   * Set function room BestFit Mode in the cart
   * This will redirect the user
   */
  function setFunctionRoomBestFitMode(selectFunctionRoomId: string, listFunctionRoomId: string[]) {
    if (!cart.value) {
      throw new Error('Cart not initialized')
    }
    if (configStore.funnel.showVenueSelection) {
      throw new Error('setFunctionRoomBestFitMode should not be called when venue is enable')
    }
    // Clear all the possible previous one
    bestFitMode.value.listFunctionRoomID.clear()
    bestFitMode.value.sortedTimeSlot = []
    // Retrieve TimeSlot (And additional information)
    for (const functionRoomId of listFunctionRoomId) {
      try {
        const functionRoom = packageStore.getDb2FunctionRoom(cart.value.packageId, functionRoomId)
        if (!functionRoom) {
          throw new Error('Could not get functionRoom')
        }
        bestFitMode.value.listFunctionRoomID.set(functionRoomId, {
          priceCorrespondence: selectFunctionRoomId === functionRoomId ? 0 : undefined,
          listTimeSlot: functionRoom.apiData.searchTimeSlot,
          listAddOnID: functionRoom.apiData.eventsIds.map((e) => e.eventClassificationId),
        } as BestFitModeData)
      } catch (e) {
        throw new Error(`BestFitMode Price Loading Failed: ${e}`)
      }
    }
    // Selected TimeSlot for the bestFit (override specific one and priories selected one)
    for (const [key, item] of bestFitMode.value.listFunctionRoomID) {
      // Always add the selected functionRoom
      if (selectFunctionRoomId === key) {
        item?.listTimeSlot.forEach((slot, index) => {
          bestFitMode.value.sortedTimeSlot.push({
            defaultSelected: index === 0,
            startTime: slot.startTime,
            endTime: slot.endTime,
            originalIndex: index,
            functionRoomId: key,
            priceCorrespondence: item?.priceCorrespondence,
            listAddOnID: item?.listAddOnID,
          })
        })
      }
    }
    // Pickup the possible other timeSlot for a different functionRoom
    for (const item of bestFitMode.value.listFunctionRoomID) {
      // (Buffer use to store temporary the new timeSlot)
      const buffer: BestFitModeTimeSlot[] = []
      // Loop over the timeSlot
      item[1]?.listTimeSlot.forEach((slot, index) => {
        // Check if startTime/endTime is not already defined
        if (
          bestFitMode.value.sortedTimeSlot.filter(
            (currSlot) =>
              currSlot.startTime === slot.startTime && currSlot.endTime === slot.endTime,
          ).length === 0
        ) {
          // If not add this new timeSlot to the buffer
          buffer.push({
            startTime: slot.startTime,
            endTime: slot.endTime,
            originalIndex: index,
            functionRoomId: item[0],
            priceCorrespondence: item[1]?.priceCorrespondence,
            listAddOnID: item[1]?.listAddOnID,
          })
        }
      })
      // Transfer "buffer" to "sortedTimeSlot"
      buffer.forEach((element) => {
        bestFitMode.value.sortedTimeSlot.push(element)
      })
    }
    // Sort all the select TimeSlot
    bestFitMode.value.sortedTimeSlot.sort((a, b): number => {
      let hourA = Number(a.startTime.split(':')[0])
      let hourB = Number(b.startTime.split(':')[0])
      let minuteA = Number(a.startTime.split(':')[1])
      let minuteB = Number(b.startTime.split(':')[1])
      let hourDiff = hourA - hourB
      // When same hour
      if (hourDiff === 0) {
        // If same time, look at the endTime
        if (minuteA === minuteB) {
          hourA = Number(a.endTime.split(':')[0])
          hourB = Number(b.endTime.split(':')[0])
          minuteA = Number(a.endTime.split(':')[1])
          minuteB = Number(b.endTime.split(':')[1])
          hourDiff = hourA - hourB
          // When same endTime hour
          if (hourDiff === 0) {
            // means exacte same startTime & endTime
            if (minuteA === minuteB) {
              return 0
            }
            // Order by chronological time from the endTime
            return minuteA <= minuteB ? -1 : 1
          }
          // Order by chronological time from the endTime
          return hourDiff
        }
        // Order by chronological time
        return minuteA <= minuteB ? -1 : 1
      }
      // Order by chronological time
      return hourDiff
    })
    cart.value.functionRoomId = selectFunctionRoomId
  }

  /**
   * Set function room in the cart
   * This will redirect the user
   */
  function setFunctionRoomSetup(functionRoomSetup: string) {
    if (!cart.value) {
      throw new Error('Cart not initialized')
    }
    cart.value.functionRoomSetup = functionRoomSetup
  }

  async function book() {
    checkoutLoading.value = true
    let hasError = false
    let isAlreadyBookedError = false

    try {
      if (!cart.value) {
        throw new Error('Cart not initialized')
      }
      if (!cart.value.functionRoomId) {
        throw new Error('Current venue missing ID')
      }
      if (!configStore.funnel.confirmationUrl) {
        throw new Error('Missing confirmation url')
      }
      if (!configStore.funnel.locationId) {
        throw new Error('Missing location ID')
      }

      const functionRoom = packageStore.getDb2FunctionRoom(
        cart.value.packageId,
        cart.value.functionRoomId,
      )
      if (!functionRoom) {
        throw new Error('Could not get function room configuration')
      }

      if (!cart.value.functionRoomSetup) {
        throw new Error('Missing function room setup')
      }

      /** Will be used on confirmation page to identify the cart */
      const cartId = uuidv4()

      const confirmationUrl = addQueryParamsToRelativeURL(configStore.funnel.confirmationUrl, {
        cart: cartId,
      })

      const bookingObject = createBooking(
        {
          date: cart.value.date,
          attendees: cart.value.attendees,
          userData: {
            company: userStore.user.company,
            firstName: userStore.user.firstName,
            lastName: userStore.user.lastName,
            email: userStore.user.email,
            phone: userStore.user.phone,
            nbGuestRooms: options.value.needGuestrooms ? options.value.guestroomsQuantity : 0,
          },
          eventName: options.value.eventName,
          comments: options.value.comments,
          packageId: cart.value.packageId,
          functionRoomId: cart.value.functionRoomId,
          functionRoomSetupName: cart.value.functionRoomSetup,
          functionRoomEvents: functionRoom.apiData.eventsIds.map((eventID) => {
            // Get right index for the timeSlot
            const indexTimeSlot =
              cart.value?.timeSlot?.index && cart.value?.timeSlot?.index >= 0
                ? cart.value?.timeSlot?.index
                : 0
            if (
              eventID.timeSlot.at(indexTimeSlot) &&
              eventID.timeSlot.at(indexTimeSlot)?.startTime &&
              eventID.timeSlot.at(indexTimeSlot)?.endTime
            ) {
              return {
                id: eventID.id,
                startTime: eventID.timeSlot.at(indexTimeSlot)?.startTime ?? '',
                endTime: eventID.timeSlot.at(indexTimeSlot)?.endTime ?? '',
                eventClassificationId: eventID.eventClassificationId,
              }
            }
            return {
              id: eventID.id,
              startTime: '',
              endTime: '',
              eventClassificationId: eventID.eventClassificationId,
            }
          }),
          selectedAddons: cart.value.addons,
        },
        confirmationUrl,
        window.galaxyInfo?.local,
        {
          xObjectID: configStore.funnel.proxyDataObjectId,
          xObjectType: configStore.funnel.proxyDataObjectType,
        },
      )

      try {
        const { paymentlink } = await getPaymentUrl(configStore.funnel.locationId, bookingObject)
        storeCartSummary(cartId)
        if (paymentlink) window.location.href = paymentlink
      } catch (error) {
        console.error(error)
        hasError = true
        // Check if the booking is not already booked (by matching the error message)
        if (
          String(error).includes(
            'Booking could not be created due to event function room conflicts',
          )
        ) {
          isAlreadyBookedError = true
        }
      }
    } catch (error) {
      console.error(error)
      hasError = true
    }

    if (hasError && configStore.funnel.confirmationUrl) {
      checkoutLoading.value = false
      setTimeout(() => {
        window.scrollTo(0, document.body.scrollHeight)
        if (!isAlreadyBookedError) {
          Array.from(document.querySelectorAll('.db-retry-error')).forEach((el) =>
            el.classList.remove('db-tw-hidden'),
          )
        } else {
          Array.from(document.querySelectorAll('.db-retry-error-booked')).forEach((el) =>
            el.classList.remove('db-tw-hidden'),
          )
          Array.from(document.querySelectorAll('.db-group-payment-submit')).forEach((el) => {
            el.classList.add('db-disabled')
            el.disabled = true
          })
        }
      })
    }
    configStore.resetIdle()
  }

  // apiRequestPrices retrieve Price information for provided functionRoomId
  async function apiRequestPrices(functionRoomId: string | undefined) {
    if (!cart.value) {
      throw new Error('Cart not initialized')
    }
    if (!functionRoomId) {
      throw new Error('Current venue missing ID')
    }
    if (!configStore.funnel.locationId) {
      throw new Error('Missing location ID')
    }

    const functionRoom = packageStore.getDb2FunctionRoom(cart.value.packageId, functionRoomId)
    if (!functionRoom || !cart.value.functionRoomSetup) {
      throw new Error('Could not get function room configuration')
    }

    const partialCart = createRequestPricingObject({
      attendees: cart.value.attendees,
      packageId: cart.value.packageId,
      functionRoomId: functionRoomId,
      functionRoomSetupName: cart.value.functionRoomSetup,
      functionRoomEvents: functionRoom.apiData.eventsIds,
      selectedAddons: cart.value.addons,
    })

    apiPricing.value.isLoading = true

    // todo: we don't handle for now the case where the call has already been done
    // then the user changes their mind
    // in reality we should abort the previous request
    // here we have a risk that the old price show instead of the new one
    return await getCartPrices(configStore.funnel.locationId, partialCart, {
      useCache: true,
      languageCode: window.galaxyInfo?.local || 'en',
    })
  }

  // This function is use to recompute the prices information for all possible functionRooms (selected or not)
  // Thread each request for the price
  // Recalculate the price in relation with the referencePrice (the selected one)
  async function computePriceBestFit(referencePrice: number, referenceFunctionRoomId: string) {
    const listIdsPriceToCompute = new Map<string, number | undefined>()
    // Look for missing price info and set selected price
    bestFitMode.value.sortedTimeSlot.forEach((timeSlot) => {
      // Set Price for the selected functRoom
      if (timeSlot.functionRoomId === referenceFunctionRoomId) {
        timeSlot.functionRoomPrice = referencePrice
        timeSlot.priceCorrespondence = 0
      }
      // Check if price data is missing for another functRoom
      if (timeSlot.priceCorrespondence === undefined || timeSlot.functionRoomPrice === undefined) {
        listIdsPriceToCompute.set(timeSlot.functionRoomId, undefined)
      }
    })
    // Check if some price must be retrieve and do so
    if (Array.from(listIdsPriceToCompute.keys()).length !== 0) {
      // Store all the request in a array
      const listPromisePriceCall: Promise<[string, number]>[] = []
      Array.from(listIdsPriceToCompute.keys()).forEach((functRoomId) => {
        // Create async request for each price info
        listPromisePriceCall.push(
          new Promise<[string, number]>((resolve, reject) => {
            apiRequestPrices(functRoomId)
              .then((response) => {
                resolve([functRoomId, response.reservationTotal])
              })
              .catch((error) => {
                reject(
                  new Error(
                    `BestFitMode: Failed to retrieve functionRoom (${functRoomId}) prices: ${error}`,
                  ),
                )
              })
          }),
        )
      })
      for (const priceCall of listPromisePriceCall) {
        try {
          // Collected all the prices info
          const [functRoomId, priceValue] = await priceCall
          listIdsPriceToCompute.set(functRoomId, priceValue)
        } catch (error) {
          console.error(error)
        }
      }
    }
    // Recalculate Price information
    bestFitMode.value.sortedTimeSlot.forEach((timeSlot) => {
      // Ignore selected referenceFunctionRoomId cause already set above
      if (timeSlot.functionRoomId !== referenceFunctionRoomId) {
        // Reset possible retrieved price
        if (listIdsPriceToCompute.has(timeSlot.functionRoomId)) {
          timeSlot.functionRoomPrice = listIdsPriceToCompute.get(timeSlot.functionRoomId)
        }
        // recompute price in relation with the selected one
        if (timeSlot.functionRoomPrice !== undefined) {
          timeSlot.priceCorrespondence =
            Math.round(Number(referencePrice - timeSlot.functionRoomPrice) * 100) / 100
        } else {
          // If a error occurred, undefined is set
          timeSlot.priceCorrespondence = undefined
        }
      }
    })
  }

  watchEffect(async () => {
    try {
      if (!cart.value) {
        throw new Error('Cart not initialized')
      }
      const referencePrice = await apiRequestPrices(cart.value.functionRoomId)

      // If BestFitMode is enable and a functionRoomId is selected
      if (!configStore.funnel.showVenueSelection && !!cart.value.functionRoomId) {
        // Recalculate all the different prices
        computePriceBestFit(referencePrice.reservationTotal, cart.value.functionRoomId)
      }
      whiteListLocationIDMessage.value = referencePrice.xWhiteListLocationIDMessage
      apiPricing.value.isLoading = false
      apiPricing.value.data = referencePrice
    } catch (error) {
      // if for any reason we can't get prices, we empty the pricing
      apiPricing.value.data = undefined
      apiPricing.value.isLoading = false
    }
  })

  function cleanCartSummary() {
    for (const cartSummaryKey in storedCartSummaries.value) {
      if (storedCartSummaries.value[cartSummaryKey]) {
        if (storedCartSummaries.value[cartSummaryKey].timestamp + cart_max_time <= Date.now()) {
          delete storedCartSummaries.value[cartSummaryKey]
        }
      }
    }
  }

  /**
   * We can not get the booking information from the API
   * ("Get Booking immediately after create will always result in no revenue being returned")
   * so we store it in localStorage to display on confiration page
   */
  function storeCartSummary(cartId: string) {
    if (!cartSummary.value) {
      throw new Error('There is no cart data to store')
    }

    storedCartSummaries.value[cartId] = {
      timestamp: +new Date(),
      cart: cartSummary.value,
    }
  }

  /**
   * Get a cart summary that was stored when doing a booking
   * Used for Confirmation page
   */
  function getStoredCartSummary(cartId: string) {
    cleanCartSummary()
    const storedData = storedCartSummaries.value[cartId] || undefined

    // because the data was serialized, we need to retransform it into a date
    if (storedData?.cart) storedData.cart.date = new Date(storedData.cart.date)
    return storedData?.cart
  }

  function $reset() {
    cart.value = null
    packageDetails.value = undefined
    packageStore.clearDeepLinking()
    // TODO: router go to package selection
  }

  const currentPackage = computed(() => {
    if (!cart.value) {
      return undefined
    }
    return packageStore.getDb2Package(cart.value?.packageId)
  })

  const currentFunctionRoom = computed(() => {
    if (!cart.value) {
      return undefined
    }
    return cart.value.functionRoomId
      ? packageStore.getDb2FunctionRoom(cart.value.packageId, cart.value.functionRoomId)
      : undefined
  })

  const currentFunctionRoomId = computed(() => {
    if (!cart.value) {
      return undefined
    }
    return cart.value.functionRoomId
  })

  const getBestFitTimeSlot = computed(() => {
    if (!cart.value) {
      return undefined
    }
    return cart.value.functionRoomId ? bestFitMode.value.sortedTimeSlot : undefined
  })

  const currencyCode = computed(() => {
    if (!cart.value) {
      return undefined
    }
    return apiPricing.value.isLoading === false
      ? apiPricing.value.data?.currencyCode || currentPackage.value?.apiData.currencyCode
      : currentPackage.value?.apiData.currencyCode
  })

  return {
    options,
    cart,
    packageEvents,
    currencyCode,
    hasCart,
    cartSummary,
    currentPackage,
    currentFunctionRoom,
    currentFunctionRoomId,
    getBestFitTimeSlot,
    isLoading,
    isCartSummaryLoading,
    whiteListLocationIDMessage,
    checkoutLoading,
    getStoredCartSummary,

    init,
    setTimeSlot,
    setFunctionRoom,
    setFunctionRoomBestFitMode,
    setFunctionRoomSetup,
    setAddons,
    book,
    $reset,
  }
})
